import { User } from "../types";

export const CALORIE_LIMITS = {
  min: 1000,
  max: 5000,
} as const;

export const ACTIVITY_MULTIPLIERS: Record<NonNullable<User["activityLevel"]>, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

export const GOAL_ADJUSTMENTS: Record<NonNullable<User["goal"]>, number> = {
  lose_weight: -500,
  maintain: 0,
  gain_muscle: 500,
};

export interface CaloriePlan {
  bmr: number;
  tdee: number;
  activityMultiplier: number;
  goalAdjustment: number;
  adjustedTarget: number;
  targetCalories: number;
  wasClamped: boolean;
  weeklyWeightChangeKg: number;
}

export function calculateCaloriePlan(profile: Partial<User>): CaloriePlan | null {
  if (
    !profile.weight ||
    !profile.height ||
    !profile.age ||
    !profile.gender ||
    !profile.activityLevel ||
    !profile.goal
  ) {
    return null;
  }

  let bmr = 10 * profile.weight + 6.25 * profile.height - 5 * profile.age;
  bmr += profile.gender === "male" ? 5 : -161;

  const activityMultiplier = ACTIVITY_MULTIPLIERS[profile.activityLevel] || ACTIVITY_MULTIPLIERS.sedentary;
  const tdee = bmr * activityMultiplier;
  const goalAdjustment = GOAL_ADJUSTMENTS[profile.goal] ?? 0;
  const adjustedTarget = tdee + goalAdjustment;
  const roundedTarget = Math.round(adjustedTarget);
  const targetCalories = Math.max(
    CALORIE_LIMITS.min,
    Math.min(CALORIE_LIMITS.max, roundedTarget),
  );

  return {
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    activityMultiplier,
    goalAdjustment,
    adjustedTarget: roundedTarget,
    targetCalories,
    wasClamped: targetCalories !== roundedTarget,
    weeklyWeightChangeKg: Number(((goalAdjustment * 7) / 7700).toFixed(1)),
  };
}
